/**
 * PlannerSnapshotSection (with UtilisationBar) — extracted from
 * DesignPackTab.js (v260.76). Pure mechanical move; no behaviour
 * change.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, CalendarCheck2, ShieldCheck } from 'lucide-react';
import { GanttChart } from '../../../components/planner/GanttChart';
import { Badge } from '../../../components/ui/badge';
import { SectionCard, fmtDate, fmtDateTime, KV } from './shared';
import logger from '../../../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * v260.75 — UtilisationBar
 * Tiny CSS-only utilisation chip rendered next to each Seertech role
 * on the Resource Plan table. Reads at a glance:
 *   • <40 %  amber  → underutilised (room to take on more)
 *   • 40-90  emerald → healthy load
 *   • 90-110 blue   → fully loaded
 *   • >110   rose   → overloaded (resourcing risk)
 * Tooltip exposes the underlying math so consultants can defend the
 * number in conversation.
 */
function UtilisationBar({ percent, hours, capacity, weeks, fte, hpw }) {
  if (percent == null || !isFinite(percent)) {
    return <span className="text-slate-300 text-[10px]">—</span>;
  }
  const clamped = Math.max(0, Math.min(percent, 130));
  let color = '#10B981';     // emerald
  let label = 'Healthy';
  if (percent < 40) { color = '#F59E0B'; label = 'Underutilised'; }
  else if (percent > 110) { color = '#EF4444'; label = 'Overloaded'; }
  else if (percent > 90) { color = '#3B82F6'; label = 'Fully loaded'; }
  const tooltip =
    `${label} — ${percent.toFixed(0)} % utilisation\n` +
    `${(hours || 0).toFixed(0)} h booked / ${(capacity || 0).toFixed(0)} h capacity\n` +
    `(${weeks || 0} wk × ${(fte || 0).toFixed(2)} FTE × ${hpw} h/wk)`;
  return (
    <span className="inline-flex items-center gap-1.5 align-middle" title={tooltip}>
      <span
        className="relative inline-block h-1.5 rounded-sm overflow-hidden bg-slate-200"
        style={{ width: 48 }}
        aria-label={tooltip}
      >
        <span
          className="absolute left-0 top-0 h-full rounded-sm transition-[width]"
          style={{ width: `${(clamped / 130) * 100}%`, background: color }}
        />
      </span>
      <span className="text-[10px] tabular-nums font-medium" style={{ color }}>
        {percent.toFixed(0)}%
      </span>
    </span>
  );
}

function PlannerSnapshotSection({ planner, estimateId }) {
  // v260.62 — Fetch the live planner timeline so we can render the
  // Gantt + resource plan inline. For draft packs this is always live;
  // for finalized packs we still fetch live for now (the snapshot
  // doesn't yet capture timeline data — sales can use Regenerate to
  // refresh if the estimate has changed since finalize).
  const [timeline, setTimeline] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [view, setView] = useState('phase'); // 'phase' | 'component'

  useEffect(() => {
    let cancelled = false;
    if (!estimateId) return undefined;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await axios.post(
          `${API_URL}/api/planner/timeline`,
          { estimate_id: estimateId },
          { withCredentials: true },
        );
        if (!cancelled) setTimeline(r.data);
      } catch (e) {
        if (!cancelled) {
          logger.warn('design-pack timeline fetch failed', e?.message);
          setError(e?.response?.data?.detail || 'Could not load timeline');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [estimateId]);

  const hasPhase = !!(timeline?.phase_schedule && timeline.phase_schedule.length > 0);
  const ganttSchedule = (view === 'phase' && hasPhase) ? timeline.phase_schedule : timeline?.group_schedule;

  return (
    <SectionCard id="planner" icon={CalendarCheck2} title="Project plan & conditions" defaultOpen={false}>
      {/* Conditions key-value */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KV label="Start Date" value={fmtDate(planner.goal_start_date)} />
        <KV label="Duration" value={planner.goal_duration_weeks ? `${planner.goal_duration_weeks} weeks` : null} />
        <KV label="Go-Live" value={fmtDate(planner.goal_go_live_date)} />
        <KV label="Hours per Day" value={planner.hours_per_day} />
        <KV label="Working Days / Week" value={planner.working_days_per_week} />
        <KV label="Currency" value={planner.currency_code} />
        <KV label="Contracting Entity" value={planner.contracting_entity} />
        <KV label="Has Project Conditions" value={planner.has_conditions ? 'Yes' : 'No'} />
      </div>

      {(planner.breaks?.length > 0 || planner.blackouts?.length > 0) && (
        <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 gap-4">
          {planner.breaks?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Breaks</div>
              <ul className="text-xs text-slate-700 space-y-1">
                {planner.breaks.map(b => (
                  <li key={b.id} className="border-l-2 border-amber-300 pl-2">
                    <span className="font-medium">{b.label || 'Break'}</span> · {fmtDate(b.start_date)} → {fmtDate(b.end_date)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {planner.blackouts?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Go-Live blackouts</div>
              <ul className="text-xs text-slate-700 space-y-1">
                {planner.blackouts.map(b => (
                  <li key={b.id} className="border-l-2 border-rose-300 pl-2">
                    <span className="font-medium">{b.label || 'Blackout'}</span> · {fmtDate(b.start_date)} → {fmtDate(b.end_date)}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Gantt + Resource plan — fetched live from /planner/timeline */}
      <div className="mt-5 pt-5 border-t border-slate-100">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[#0F2040]">Schedule</div>
          {hasPhase && (
            <div className="flex items-center gap-1 text-[10px]" data-testid="design-pack-gantt-view-toggle">
              <button
                type="button"
                onClick={() => setView('phase')}
                className={`px-2 py-0.5 rounded-md border ${view === 'phase' ? 'bg-[#0F2040] text-white border-[#0F2040]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
              >Phase</button>
              <button
                type="button"
                onClick={() => setView('component')}
                className={`px-2 py-0.5 rounded-md border ${view === 'component' ? 'bg-[#0F2040] text-white border-[#0F2040]' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`}
              >Component</button>
            </div>
          )}
        </div>
        {loading ? (
          <div className="py-8 flex items-center justify-center text-slate-400 text-xs">
            <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading timeline…
          </div>
        ) : error ? (
          <div className="rounded-md border border-rose-200 bg-rose-50 p-2.5 text-[11px] text-rose-700">{error}</div>
        ) : !timeline ? (
          <div className="text-xs text-slate-400 italic">No timeline available — set project conditions first.</div>
        ) : (
          <div className="overflow-x-auto" data-testid="design-pack-gantt">
            <GanttChart
              schedule={ganttSchedule}
              phaseSchedule={timeline.phase_schedule}
              totalWeeks={timeline.total_duration_weeks}
              planMode={timeline.plan_mode}
              milestones={timeline.milestones}
              estimateId={estimateId}
              startDate={planner.goal_start_date || timeline.goal_start_date || null}
              defaultMilestonesCollapsed={timeline?.ui_defaults?.gantt_milestones_collapsed}
              releases={null}
              editable={false}
              conditions={null}
            />
          </div>
        )}
      </div>

      {/* Resource plan — Seertech side (role_allocation) + Client side
          (client_resources). Two side-by-side tables so leadership can
          eyeball the staffing commitment from both organisations. */}
      {(timeline && ((timeline.role_allocation && timeline.role_allocation.length > 0) || (timeline.client_resources && timeline.client_resources.length > 0))) && (
        <div className="mt-5 pt-5 border-t border-slate-100">
          <div className="text-[11px] font-bold uppercase tracking-wider text-[#0F2040] mb-2">Resource plan</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {timeline.role_allocation && timeline.role_allocation.length > 0 && (
              <div data-testid="design-pack-resource-seertech">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5 font-semibold">Seertech</div>
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-100">
                      <th className="py-1 pr-2">Role</th>
                      <th className="py-1 pr-2 text-right">Hours</th>
                      <th className="py-1 pr-2 text-right">FTE</th>
                      <th className="py-1 pr-2 text-right">Weeks</th>
                      <th className="py-1 pr-2 text-right">Utilisation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timeline.role_allocation.map((r, i) => {
                      // v260.74 — planner timeline emits `effort_hours`
                      // (not `hours`) and `scheduled_weeks` (not
                      // `weeks`). Fall back through both shapes so the
                      // table populates for any caller variant.
                      const hours = r.effort_hours ?? r.hours ?? r.total_hours;
                      const weeks = r.scheduled_weeks ?? r.weeks ?? r.weeks_needed;
                      // v260.75 — Per-role utilisation chip so leadership
                      // can see who's overloaded vs underutilised at a
                      // glance. Formula: effort_hours / (scheduled_weeks
                      // × FTE × hours_per_week). Pulls hours_per_week
                      // from the planner's working assumptions; falls
                      // back to a 40h convention if absent.
                      const hpw = timeline.working_assumptions?.hours_per_week || 40;
                      const capacity = (weeks || 0) * (r.fte || 0) * hpw;
                      const util = (hours && capacity > 0) ? (hours / capacity) * 100 : null;
                      return (
                        <tr key={i} className="border-b border-slate-50">
                          <td className="py-1 pr-2 text-[#0F2040] font-medium">{r.role_label || r.role || r.name || '—'}</td>
                          <td className="py-1 pr-2 text-right text-slate-700">{hours != null ? Number(hours).toFixed(0) : '—'}</td>
                          <td className="py-1 pr-2 text-right text-slate-700">{r.fte != null ? Number(r.fte).toFixed(2) : '—'}</td>
                          <td className="py-1 pr-2 text-right text-slate-700">{weeks ?? '—'}</td>
                          <td className="py-1 pl-2 text-right" data-testid={`design-pack-utilisation-${r.role || i}`}>
                            <UtilisationBar percent={util} hours={hours} capacity={capacity} weeks={weeks} fte={r.fte} hpw={hpw} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {timeline.client_resources && timeline.client_resources.length > 0 && (
              <div data-testid="design-pack-resource-client">
                <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1.5 font-semibold">Client</div>
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-100">
                      <th className="py-1 pr-2">Role</th>
                      <th className="py-1 pr-2 text-right">Hours</th>
                      <th className="py-1 pr-2 text-right">FTE</th>
                      <th className="py-1 pr-2 text-right">Weeks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timeline.client_resources.map((r, i) => {
                      // v260.74 — client_resources only exposes the
                      // weekly_pct map; the planner now derives
                      // total_hours / avg_fte / active_weeks for the
                      // Design Pack so this table mirrors the Seertech
                      // shape.
                      const hours = r.total_hours ?? r.effort_hours ?? r.hours;
                      const fte = r.avg_fte ?? r.fte;
                      const weeks = r.active_weeks ?? r.scheduled_weeks ?? r.weeks;
                      return (
                        <tr key={i} className="border-b border-slate-50">
                          <td className="py-1 pr-2 text-[#0F2040] font-medium">{r.label || r.role || r.name || '—'}</td>
                          <td className="py-1 pr-2 text-right text-slate-700">{hours != null ? Number(hours).toFixed(0) : '—'}</td>
                          <td className="py-1 pr-2 text-right text-slate-700">{fte != null ? Number(fte).toFixed(2) : '—'}</td>
                          <td className="py-1 pr-2 text-right text-slate-700">{weeks ?? '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </SectionCard>
  );
}

function ApprovalAuditSection({ events }) {
  if (!events?.length) return null;
  return (
    <SectionCard id="approvals" icon={ShieldCheck} title="Sign-off audit trail" count={events.length} defaultOpen={false}>
      <ul className="space-y-1.5">
        {events.map((e, i) => (
          <li key={i} className="text-xs text-slate-700 flex items-start gap-2">
            <span className="text-slate-400 flex-shrink-0 w-32">{fmtDateTime(e.at)}</span>
            <Badge className={`text-[9px] py-0 ${
              e.kind === 'approved' ? 'bg-emerald-100 text-emerald-700' :
              e.kind === 'changes_requested' ? 'bg-amber-100 text-amber-700' :
              'bg-slate-100 text-slate-600'
            }`}>{e.kind.replace('_', ' ')}</Badge>
            <span className="min-w-0 flex-1">
              <span className="font-medium text-[#0F2040]">{e.name || e.to}</span>
              {e.comment && <span className="italic text-slate-500"> — “{e.comment}”</span>}
            </span>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}


export { UtilisationBar, ApprovalAuditSection };
export default PlannerSnapshotSection;
